import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../utils/api';
import Skeleton from './common/Skeleton';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const gradeColors = {
  A: 'success',
  B: 'info',
  C: 'warning',
  D: 'orange',
  F: 'danger',
};

const severityIcon = { critical: '🔴', warning: '🟡', info: '🔵' };

function formatRatio(value, total) {
  if (!total) return `0/0 (0%)`;
  const pct = Math.round((value / total) * 1000) / 10;
  return `${value}/${total} (${pct}%)`;
}

function ScoreCircle({ score, grade }) {
  const gradeClass = (grade || 'F').toLowerCase();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
      <div className={`ce-grade ce-grade--${gradeClass}`}>{grade}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{score}/100</div>
    </div>
  );
}

function DimensionCard({ dim, t }) {
  const pct = dim.score;
  const fillCls = pct >= 80 ? 'ce-score-bar-fill--green' : pct >= 60 ? 'ce-score-bar-fill--yellow' : 'ce-score-bar-fill--red';
  const label = t(`quality.dim.${dim.name}`, dim.name);
  return (
    <div className="ce-card">
      <div className="ce-card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{label}</span>
          <span className="ce-tag">{dim.weight}% {t('quality.weight')}</span>
        </div>
        <div className="ce-score-bar" style={{ marginBottom: '0.5rem' }}>
          <div className={`ce-score-bar-fill ${fillCls}`} style={{ width: `${pct}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
          <span style={{ color: 'var(--bs-secondary-color)' }}>{dim.description}</span>
          <span style={{ fontWeight: 700 }}>{pct}%</span>
        </div>
        {dim.packagesTotal > 0 && (
          <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginTop: '0.25rem' }}>
            {dim.packagesOk}/{dim.packagesTotal} packages
          </div>
        )}
      </div>
    </div>
  );
}

export default function SbomQuality({ repoName, sbomName, initialData }) {
  const { t } = useTranslation();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [showNtia, setShowNtia] = useState(true);

  useEffect(() => {
    if (initialData !== undefined) return;
    if (!repoName || !sbomName) return;
    setLoading(true);
    setError(null);
    setData(null);
    const sbomFile = sbomName.endsWith('-sbom.json') ? sbomName : `${sbomName}-sbom.json`;
    authFetch(`${API_BASE}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(sbomFile)}/quality`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [repoName, sbomName]);

  useEffect(() => {
    if (initialData === undefined) return;
    setData(initialData || null);
    setLoading(false);
    if (initialData) setError(null);
  }, [initialData]);

  if (loading) {
    return (
      <div style={{ padding: '1rem 0' }}>
        <Skeleton.Card />
        <div className="small text-secondary mt-3">{t('quality.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3">
        <strong>{t('quality.error')}:</strong> {error}
      </div>
    );
  }

  if (!data) return null;

  const gradeVariant = gradeColors[data.grade] || 'secondary';
  const gradeBsClass = gradeVariant === 'orange' ? 'warning' : gradeVariant;

  const criticalIssues = (data.issues || []).filter(i => i.severity === 'critical');
  const warningIssues = (data.issues || []).filter(i => i.severity === 'warning');
  const infoIssues = (data.issues || []).filter(i => i.severity === 'info');
  const identifierQuality = data.identifierQuality || {};
  const coveredPackages = identifierQuality.coveredPackages || 0;

  return (
    <div className="px-1">
      {/* Header card */}
      <div className="ce-card" style={{ marginBottom: '1rem' }}>
        <div className="ce-card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.25rem' }}>
            <ScoreCircle score={data.overallScore} grade={data.grade} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', marginBottom: '0.5rem' }}>{t('quality.title')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <span className={`ce-sev ce-sev--${gradeBsClass === 'success' ? 'low' : gradeBsClass === 'info' ? 'info' : gradeBsClass === 'warning' ? 'medium' : 'critical'}`}>
                  {t('quality.grade')}: {data.grade}
                </span>
                <span
                  className={`ce-sev ${data.ntiaCompliant ? 'ce-sev--low' : 'ce-sev--critical'}`}
                  style={{ cursor: data.ntiaCompliant ? 'default' : 'pointer' }}
                  onClick={() => !data.ntiaCompliant && setShowNtia(v => !v)}
                >
                  {t('quality.ntia')}: {data.ntiaCompliant ? t('quality.ntiaYes') : `${t('quality.ntiaNo')} ▾`}
                </span>
                <span className="ce-tag">{data.totalPackages} {t('quality.packages')}</span>
              </div>
            </div>
          </div>
          {data.summary && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bs-tertiary-bg)', borderRadius: '8px', fontSize: '0.8125rem' }}>
              <strong>{t('quality.summary')}:</strong> {data.summary}
            </div>
          )}
        </div>
      </div>

      <div className="ce-card" style={{ marginBottom: '1rem' }}>
        <div className="ce-card-header">
          <h6 className="ce-card-title">{t('quality.identifiersTitle')}</h6>
          <span className="ce-tag">
            {formatRatio(coveredPackages, data.totalPackages)} {t('quality.identifiersCovered')}
          </span>
        </div>
        <div className="ce-card-body">
          <div className="ce-grid-4">
            <div style={{ border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>{t('quality.validPurl')}</div>
              <div style={{ fontWeight: 700, fontSize: '1.125rem' }}>{identifierQuality.validPurl || 0}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{formatRatio(identifierQuality.validPurl || 0, data.totalPackages)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>+{identifierQuality.enrichedPurl || 0} {t('quality.enriched')}</div>
            </div>
            <div style={{ border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>{t('quality.validCpe')}</div>
              <div style={{ fontWeight: 700, fontSize: '1.125rem' }}>{identifierQuality.validCpe || 0}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{formatRatio(identifierQuality.validCpe || 0, data.totalPackages)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>+{identifierQuality.enrichedCpe || 0} {t('quality.enriched')}</div>
            </div>
            <div style={{ border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>{t('quality.invalidIdentifiers')}</div>
              <div style={{ fontWeight: 700, fontSize: '1.125rem' }}>{(identifierQuality.invalidPurl || 0) + (identifierQuality.invalidCpe || 0)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{formatRatio((identifierQuality.invalidPurl || 0) + (identifierQuality.invalidCpe || 0), data.totalPackages)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>PURL: {identifierQuality.invalidPurl || 0} · CPE: {identifierQuality.invalidCpe || 0}</div>
            </div>
            <div style={{ border: '1px solid var(--bs-border-color)', borderRadius: '8px', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>{t('quality.missingIdentifiers')}</div>
              <div style={{ fontWeight: 700, fontSize: '1.125rem' }}>{identifierQuality.missingBoth || 0}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{formatRatio(identifierQuality.missingBoth || 0, data.totalPackages)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>{t('quality.missingIdentifiersHint')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* NTIA Checklist — shown when not compliant or when manually expanded */}
      {(data.ntiaChecks || []).length > 0 && (!data.ntiaCompliant || showNtia) && (
        <div className="ce-card" style={{ marginBottom: '1rem' }}>
          <div className="ce-card-header" style={{ cursor: 'pointer' }} onClick={() => setShowNtia(v => !v)}>
            <h6 className="ce-card-title">🗂️ NTIA Minimum Elements Checklist</h6>
            <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
              {(data.ntiaChecks || []).filter(c => c.pass).length}/{(data.ntiaChecks || []).length} ✔
              &nbsp;{showNtia ? '▴' : '▾'}
            </span>
          </div>
          {showNtia && (
            <div className="ce-card-body--flush">
              <div className="ce-table-wrap">
                <table className="ce-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>{t('quality.ntiaElement', 'Element')}</th>
                      <th>{t('quality.ntiaDetail', 'Detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.ntiaChecks || []).map((check, i) => (
                      <tr key={i} style={!check.pass ? { background: 'rgba(220,38,38,0.05)' } : {}}>
                        <td style={{ textAlign: 'center' }}>{check.pass ? '✅' : '❌'}</td>
                        <td style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{check.element}</td>
                        <td style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)' }}>
                          {check.detail}
                          {check.count > 0 && (
                            <span className="ce-sev ce-sev--critical" style={{ marginLeft: '0.5rem' }}>{check.count} packages</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dimensions */}
      <div className="ce-section-header">
        <h6 className="ce-section-title">{t('quality.dimensions')}</h6>
      </div>
      <div className="ce-grid-3" style={{ marginBottom: '1rem' }}>
        {(data.dimensions || []).map(dim => (
          <DimensionCard key={dim.name} dim={dim} t={t} />
        ))}
      </div>

      {/* Issues */}
      <div className="ce-section-header">
        <h6 className="ce-section-title">{t('quality.issues')}</h6>
      </div>
      {(data.issues || []).length === 0 ? (
        <div className="ce-blank" style={{ padding: '2rem 1rem' }}>
          <div className="ce-blank-icon">✅</div>
          <div className="ce-blank-title">{t('quality.noIssues')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
          {[...criticalIssues, ...warningIssues, ...infoIssues].map((issue, idx) => {
            const sevCls = issue.severity === 'critical' ? 'critical' : issue.severity === 'warning' ? 'medium' : 'info';
            return (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                padding: '0.75rem', borderRadius: '8px',
                background: 'var(--bs-tertiary-bg)',
                border: '1px solid var(--bs-border-color)',
              }}>
                <span>{severityIcon[issue.severity] || '⚪'}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.8125rem' }}>{issue.message}</span>
                </div>
                {issue.count > 0 && <span className={`ce-sev ce-sev--${sevCls}`}>{issue.count}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
